import type { LucideIcon } from 'lucide-react';
import { Play } from 'lucide-react';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';

export type SmartStartTone = 'indigo' | 'amber' | 'slate' | 'emerald';

interface SmartStartCardProps {
  /** Optional leading icon. Omitted on the Practice Arena setup cards. */
  icon?: LucideIcon;
  label: string;
  /** Concept count for this scope. `null` while we don't know; `0` shows the
   *  card in a muted "empty" state but still allows the click. */
  count: number | null;
  /** Estimated session duration, in minutes. Rendered next to the count. */
  estimatedMinutes?: number | null;
  /** First few concept names that will be practiced. The card joins them
   *  with `, ` and adds a `+N more` suffix when `count` exceeds the
   *  preview length. */
  previewConcepts?: string[];
  /** Show a skeleton-style placeholder while the preview query is
   *  resolving. Falls back to no preview when omitted. */
  isPreviewLoading?: boolean;
  subtitle?: string;
  recommended?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  /** Visual tone - scope-specific accent so the three cards read
   *  distinctly at a glance. Default: `indigo`. */
  tone?: SmartStartTone;
  /** Optional keyboard-shortcut hint (e.g. `Enter`). Renders as a small
   *  kbd badge at the bottom of the card. */
  shortcutHint?: string;
  onClick: () => void;
}

const TONE_STYLES: Record<
  SmartStartTone,
  {
    iconBg: string;
    hoverBorder: string;
    recommendedBg: string;
  }
> = {
  indigo: {
    iconBg: 'bg-brand-50 text-brand-600 dark:tint-brand',
    hoverBorder:
      'hover:border-brand-300/70 hover:shadow-lg dark:hover:border-primary/70',
    recommendedBg: 'bg-brand-100 text-brand-700 dark:tint-brand-strong',
  },
  amber: {
    iconBg:
      'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    hoverBorder:
      'hover:border-amber-300/70 hover:shadow-lg dark:hover:border-amber-700/70',
    recommendedBg:
      'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  slate: {
    iconBg:
      'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
    hoverBorder:
      'hover:border-neutral-300/70 hover:shadow-lg dark:hover:border-neutral-600/70',
    recommendedBg:
      'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  },
  emerald: {
    iconBg:
      'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    hoverBorder:
      'hover:border-emerald-300/70 hover:shadow-lg dark:hover:border-emerald-700/70',
    recommendedBg:
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
};

export function SmartStartCard({
  icon: Icon,
  label,
  count,
  estimatedMinutes,
  previewConcepts,
  isPreviewLoading,
  subtitle,
  recommended,
  disabled,
  isLoading,
  tone = 'indigo',
  shortcutHint,
  onClick,
}: SmartStartCardProps) {
  const effectiveDisabled = !!disabled;
  const showLoading = !!isLoading;
  // The card only advertises itself as actionable (Play button, Recommended
  // badge, hover lift, shortcut) once its data has actually resolved. While
  // loading we render a neutral shimmer instead - this is what prevents the
  // "Play icon flashes, then the card greys out" flicker: previously
  // `disabled` defaulted to false during load, so the live affordances
  // rendered optimistically and then retracted when a 0-count landed.
  const interactive = !effectiveDisabled && !showLoading;
  const t = TONE_STYLES[tone];
  const previewText = formatPreview(previewConcepts, count);
  const phone = useMediaQuery('(max-width: 639.98px)');

  if (phone) {
    return (
      <SmartStartRow
        label={label}
        count={count}
        estimatedMinutes={estimatedMinutes}
        line={previewText ?? subtitle}
        recommended={recommended && interactive}
        loading={showLoading || !!isPreviewLoading}
        interactive={interactive}
        disabled={effectiveDisabled}
        onClick={onClick}
      />
    );
  }

  return (
    <button
      type='button'
      onClick={onClick}
      disabled={!interactive}
      aria-busy={showLoading}
      className={`group relative flex flex-col items-start gap-2.5 rounded-2xl border bg-card/70 p-5 text-left shadow backdrop-blur-sm transition-all dark:bg-card ${
        showLoading
          ? 'cursor-default border-border/60'
          : effectiveDisabled
            ? 'cursor-not-allowed border-border/60 opacity-60'
            : `border-border/60 hover:-translate-y-0.5 ${t.hoverBorder}`
      }`}
    >
      {recommended && interactive && (
        <span className='dark:tint-brand-strong absolute right-4 top-4 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-brand-700'>
          Recommended
        </span>
      )}

      {Icon && (
        <span
          className={`flex h-10 w-10 items-center justify-center rounded-lg ${t.iconBg}`}
        >
          <Icon className='h-5 w-5' />
        </span>
      )}

      <div className='text-base font-medium text-foreground'>{label}</div>

      {/* Headline: big count + ~N min underneath. While loading, render
          a skeleton block in the same dimensions so the card chrome
          doesn't shift when counts resolve. */}
      <div className='flex flex-col gap-1'>
        {isLoading ? (
          <>
            <div className='h-9 w-12 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
            <div className='h-2.5 w-16 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
          </>
        ) : (
          <>
            <div className='flex items-baseline gap-1.5'>
              <span className='text-4xl font-semibold tabular-nums leading-none text-foreground'>
                {count == null ? '-' : count}
              </span>
              <span className='text-xs text-muted-foreground'>
                {count === 1 ? 'concept' : 'concepts'}
              </span>
            </div>
            {estimatedMinutes != null && estimatedMinutes > 0 && (
              <span className='text-xs tabular-nums text-muted-foreground'>
                ~{estimatedMinutes} min
              </span>
            )}
          </>
        )}
      </div>

      {/* Subtitle (used as fallback when there's no preview list) */}
      {subtitle && !previewText && !isPreviewLoading && !showLoading && (
        <p className='line-clamp-2 text-xs text-muted-foreground'>{subtitle}</p>
      )}

      {/* Concept-name preview */}
      {isPreviewLoading || showLoading ? (
        <div className='w-full space-y-1.5'>
          <div className='h-2.5 w-3/4 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
          <div className='h-2.5 w-1/2 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
        </div>
      ) : previewText ? (
        <p className='line-clamp-2 text-xs leading-relaxed text-muted-foreground'>
          {previewText}
        </p>
      ) : null}

      {shortcutHint && interactive && (
        <div className='mt-auto'>
          <kbd className='inline-flex h-5 items-center rounded-sm border border-gray-300 bg-muted px-1 font-mono text-xs font-medium text-muted-foreground dark:border-gray-600'>
            {shortcutHint}
          </kbd>
        </div>
      )}

      {interactive && (
        <span
          aria-hidden='true'
          className='absolute bottom-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform group-hover:scale-110'
        >
          <Play className='h-3.5 w-3.5 fill-current' />
        </span>
      )}
    </button>
  );
}

/**
 * The card on a phone: one compact row. Stacked as cards, the five practice
 * choices were about 270px each - three screens to see them all - and the
 * Play disc, pinned in the corner, sat over the end of the concept preview
 * ("... + 10 mor▶"). Here the count and minutes share a line, the preview
 * takes one, and Play has a column of its own.
 */
function SmartStartRow({
  label,
  count,
  estimatedMinutes,
  line,
  recommended,
  loading,
  interactive,
  disabled,
  onClick,
}: {
  label: string;
  count: number | null;
  estimatedMinutes?: number | null;
  line?: string | null;
  recommended?: boolean;
  loading: boolean;
  interactive: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const unit = count === 1 ? 'concept' : 'concepts';
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={!interactive}
      aria-busy={loading}
      className={`group relative isolate flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-left shadow-elevation-1 transition-transform duration-fast active:scale-[0.99] motion-reduce:active:scale-100 ${
        disabled ? 'cursor-not-allowed opacity-60' : ''
      }`}
    >
      <div className='min-w-0 flex-1'>
        <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
          <span className='text-base font-medium text-foreground'>{label}</span>
          {recommended && (
            <span className='font-mono text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground'>
              Recommended
            </span>
          )}
        </div>
        {loading ? (
          <div className='mt-2 space-y-1.5'>
            <div className='h-3 w-24 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
            <div className='h-3 w-40 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
          </div>
        ) : (
          <>
            <p className='mt-0.5 text-sm tabular-nums text-muted-foreground'>
              <span className='font-semibold text-foreground'>
                {count == null ? '-' : count}
              </span>{' '}
              {unit}
              {estimatedMinutes != null && estimatedMinutes > 0 && (
                <> · ~{estimatedMinutes} min</>
              )}
            </p>
            {line && (
              <p className='mt-0.5 truncate text-xs text-muted-foreground'>
                {line}
              </p>
            )}
          </>
        )}
      </div>
      {interactive && (
        <span
          aria-hidden='true'
          className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'
        >
          <Play className='h-3.5 w-3.5 fill-current' />
        </span>
      )}
    </button>
  );
}

function formatPreview(
  previewConcepts: string[] | undefined,
  count: number | null
): string | null {
  if (!previewConcepts || previewConcepts.length === 0) return null;
  const shown = previewConcepts.slice(0, 2).join(', ');
  const remaining = (count ?? previewConcepts.length) - 2;
  if (remaining > 0) return `${shown} + ${remaining} more`;
  return shown;
}
